import { useSitemapImport } from '@components/App/Modal/Import/hooks/useSitemapImport'
import Recaptcha from '@components/App/RecaptchaWidget'
import { IconLeft } from '@lib/icons'
import { Button } from '@octopus-do/octopus-storybook'
import { openModal } from '@storeOld/reducers/ui'
import cx from 'classnames'
import type React from 'react'
import { useState } from 'react'
import { useDispatch } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import {
  type ImportSitemapData,
  ImportSitemapResponseTypeUrlset,
  type ImportSitemapResultUrlItem,
} from '@/types'
import { IMPORT_PROJECT } from '../../../../../../constants'
import List from './components/List/List'
import css from './MultipleSitemap.module.scss'

type Props = {
  closeButton: React.ReactNode
  url: string | null
  urlsList: string[]
  meta: boolean
  // The import is landing in the project the user has open. Only Back needs it here:
  // where the pages go is already decided by the handleCommit passed in.
  intoOpenProject?: boolean
  handleCommit: (result: ImportSitemapData) => Promise<void>
}

const MultipleSitemap: React.FC<Props> = ({
  closeButton,
  url,
  urlsList,
  meta,
  intoOpenProject = false,
  handleCommit,
}) => {
  const dispatch = useDispatch()

  const [selectedUrls, setSelectedUrls] = useState<string[]>([])
  const isAllListSelected = selectedUrls.length === urlsList.length
  const [isLoading, setIsLoading] = useState(false)

  const { importUrls } = useSitemapImport()

  const containerClassName = cx(css.container)

  // Reopening the import modal bare would drop the fact that we are inside a project:
  // the next import would then take the new-project path and wipe the open one.
  const handleBackModal = () => {
    dispatch(
      openModal(
        IMPORT_PROJECT,
        // @ts-expect-error - openModal lives in untyped legacy js
        intoOpenProject ? { isLocked: true, intoOpenProject: true } : null
      )
    )
  }

  const handleSelectItem = (id: string) => {
    setSelectedUrls(
      selectedUrls.includes(id)
        ? selectedUrls.filter((selectedUrl) => id !== selectedUrl)
        : [...selectedUrls, id]
    )
  }

  const handleUnSelectAll = () => {
    setSelectedUrls([])
  }

  const handleSelectAll = () => {
    setSelectedUrls(urlsList)
  }

  const onSubmit = () => {
    if (isLoading || selectedUrls.length === 0) {
      return
    }

    setIsLoading(true)

    if (selectedUrls.length > 1) {
      sendGtmEvent(
        GtmTriggerName.CRAWL_SITEMAP_MULTI,
        `importing ${selectedUrls.length} pages`
      )
    }

    const onComplete = (result: ImportSitemapData[]) => {
      const urlsets = result.filter(
        (payload) => payload.type === ImportSitemapResponseTypeUrlset
      )

      const urlset: ImportSitemapResultUrlItem[] = []
      urlsets.forEach((item) => {
        urlset.push(...item.data)
      })

      handleCommit({
        ...urlsets[0],
        data: urlset,
        // The pages of every chosen file go in as one list, so the limit is exceeded if
        // any of them hit it — reading it off the first file alone hid the warning.
        limitExceeded: urlsets.some((item) => item.limitExceeded),
      }).catch(() => {
        // Nothing was added and the reason has already been shown. Give the button back
        // instead of leaving it spinning on a screen that is still open.
        setIsLoading(false)
      })
    }

    const onError = (err: any) => {
      console.error(err)
      setIsLoading(false)
    }

    importUrls({
      urls: selectedUrls,
      options: { meta },
      onComplete,
      onError,
    })
  }

  return (
    <Recaptcha>
      <div className={containerClassName}>
        <div className={css.back} onClick={handleBackModal}>
          <IconLeft />
          <span>Back</span>
        </div>
        <div className={css.close}>{closeButton}</div>

        <div className={css.title}>We found multiple sitemaps</div>
        <div className={css.description}>
          <span>{url || 'This file'}</span> contains multiple sitemap.xml files,
          please choose sitemaps for import:
        </div>

        <div
          className={css.selectAll}
          onClick={isAllListSelected ? handleUnSelectAll : handleSelectAll}
        >
          {isAllListSelected ? 'Uns' : 'S'}elect all
        </div>

        <List
          urls={urlsList}
          onClick={handleSelectItem}
          selectedUrls={selectedUrls}
        />

        {/* We will add this feature later after product investigation */}
        {/* {selectedIds.length > 1 && ( */}
        {/*  <div> */}
        {/*    <div className={css.selectorsTitle}>Place it:</div> */}
        {/*    <Selectors */}
        {/*      onChange={onChangeSelector} */}
        {/*      items={[ */}
        {/*        { */}
        {/*          id: SELECTORS.ONE_TREE, */}
        {/*          label: 'One tree', */}
        {/*          icon: <OneTree />, */}
        {/*        }, */}
        {/*        { */}
        {/*          id: SELECTORS.EACH_XML, */}
        {/*          label: 'Each xml to section', */}
        {/*          icon: <EachXml />, */}
        {/*        }, */}
        {/*      ]} */}
        {/*    /> */}
        {/*  </div> */}
        {/* )} */}

        <Button
          className={css.button}
          label="Continue"
          loading={isLoading}
          disabled={selectedUrls.length === 0}
          onClick={() => onSubmit()}
        />
      </div>
    </Recaptcha>
  )
}

export default MultipleSitemap
